import {
  Sailing,
  BareSailingDay,
  SailingDay,
  SimpleSailing,
  BareSimpleSailingDay,
  SimpleSailingDay,
  BareSailing,
  BareSimpleSailing,
  SailingDayBundle,
} from '../types/sailing';
import { Property } from '../types/property';

import {
  Template,
  SailingTemplate,
  SimpleTemplate,
  SimpleSailingTemplate,
} from '../types/template';

import { BareFeedTemplate } from '../types/feedTemplate';
import { BareEventTemplate } from '../types/eventTemplate';
import { BareVenueTemplate } from '../types/venueTemplate';
import { BarePlan } from '../types/plan';
import { mapBy, notUndefined } from './functional';
import { translateEventTemplate } from './eventTemplate';
import {
  AttributeTranslation,
  CommonTextTranslation,
  ContentTemplateTranslation,
  DayThemeTranslation,
  EventTemplateTranslation,
  HighlightTranslation,
  LevelTranslation,
  MealPeriodTranslation,
  SegmentTranslation,
  VenueTemplateTranslation,
  VenueTypeTranslation,
} from '../types/translation';
import { clamp, safeIdCompare } from './utils';
import { translateVenueTemplate } from './venueTemplate';
import { evaluateMergedVars } from './mergedVars';
import { getPropertyConfiguration } from './propertyConfig';
import {
  addMinutesToTime,
  dateToTime,
  getDelayedGODate,
  minutesToTimeString,
  timeCompare,
  timeToMinutes,
} from './time';
import {
  generateExtendedTimeDescription,
  getEventVenueDisplay,
  makeBareEventSorter,
} from './event';
import { getHourFormat } from './property';
import {
  getVenueAvailabilityStrings,
  getVenueAvailabilityTimePairs,
  getVenueDisplay,
  getVenueStatus,
  makeEmptyVenueDisposition,
} from './venue';
import { evaluateMcmEndpoint } from './mcm';
import { translateBareContentTemplate } from './contentTemplate';
import { BareContentTemplate } from '../types/contentTemplate';
import {
  getCommonTexts,
  translateBrandAttribute,
  translateBrandDayTheme,
  translateBrandHighlight,
  translateBrandLevel,
  translateBrandSegment,
  translateBrandVenueType,
} from './brand';
import { evaluateAudienceTruthyAggregate, evaluateAudienceZone } from './audience';
import { translateMealPeriod } from './mealPeriods';
import {
  GetDailyProgramSettings,
  DailyProgram,
  DailyProgramEvent,
  DailyProgramVenue,
  DailyProgramOffer,
  DailyProgramMessage,
  DailyProgramInformation,
  DailyProgramImage,
  DailyProgramLegacyContent,
  AbstractAreaBundle,
} from '../types/dailyProgram';
import { GO_PUBLISHER_ROLE } from '../types/info';

import { BareImage, ImageRequirement } from '../types/image';
import { getDailyProgramImageOptimizations, makeDailyProgramImages } from './image';
import { getPlanPublishedData } from './plan';
import { findFeedContent, findFeedEventContent } from './feed';
import { evaluateCallToActions } from './libraries';

export function populateDay(bareDay: BareSailingDay, templates: Template[]): SailingDay {
  let template;

  if (bareDay.template) {
    template = templates.find(t => t._id === bareDay.template);
    if (!template) {
      // TODO: This should probably be an error! But we just keep a message to
      // avoid things crashing
      console.error(`Could not find template ${bareDay.template}`);
    }
  }

  return {
    ...bareDay,
    template,
  };
}

export function populateSimpleDay(
  bareDay: BareSimpleSailingDay,
  templates: SimpleTemplate[]
): SimpleSailingDay {
  let template;

  if (bareDay.template) {
    template = templates.find(t => t._id === bareDay.template);
    if (!template) {
      // TODO: This should probably be an error! But we just keep a message to
      // avoid things crashing
      console.error(`Could not find template ${bareDay.template}`);
    }
  }

  return {
    ...bareDay,
    template,
  };
}

export function populate(
  bareSailing: BareSailing,
  property: Property,
  templates: Template[],
  sailingTemplates: SailingTemplate[]
): Sailing {
  let template;
  if (bareSailing.template) {
    template = sailingTemplates.find(t => t._id === bareSailing.template);
    if (!template) {
      // TODO: This should probably be an error! But we just keep a message to
      // avoid things crashing
      console.error(`Could not find sailing template ${bareSailing.template}`);
    }
  }

  return {
    ...bareSailing,
    property,
    template,
    days: bareSailing.days.map(bareDay => populateDay(bareDay, templates)),
  };
}

export function populateSimple(
  bareSailing: BareSimpleSailing,
  property: Property,
  templates: SimpleTemplate[],
  sailingTemplates: SimpleSailingTemplate[]
): SimpleSailing {
  let template;
  if (bareSailing.template) {
    template = sailingTemplates.find(t => t._id === bareSailing.template);
    if (!template) {
      // TODO: This should probably be an error! But we just keep a message to
      // avoid things crashing
      console.error(`Could not find sailing template ${bareSailing.template}`);
    }
  }

  return {
    ...bareSailing,
    property,
    template,
    days: bareSailing.days.map(bareDay => populateSimpleDay(bareDay, templates)),
  };
}

export function bareSailingDay(sailingDay: SailingDay): BareSailingDay {
  let template = sailingDay.template ? sailingDay.template._id : undefined;

  return {
    ...sailingDay,
    template,
  };
}

export function bareSimpleSailingDay(sailingDay: SimpleSailingDay): BareSimpleSailingDay {
  let template = sailingDay.template ? sailingDay.template._id : undefined;

  return {
    ...sailingDay,
    template,
  };
}

export function unsimplifyBareSailingDay(
  sailingDay: BareSimpleSailingDay,
  feeds: BareFeedTemplate[] | undefined
): BareSailingDay {
  let feedsToUse = feeds;

  if (!feedsToUse) {
    // TODO: This should probably be an error! But we just keep a message to
    // avoid things crashing
    console.error(`Tried to unsimplify a bare sailing day without providing a feed!`);
    feedsToUse = [] as BareFeedTemplate[];
  }
  return {
    ...sailingDay,
    feeds: feedsToUse,
  };
}

export function bare(sailing: Sailing): BareSailing {
  return {
    ...sailing,
    property: sailing.property._id,
    template: sailing.template ? sailing.template._id : undefined,
    days: sailing.days.map(bareSailingDay),
  };
}

export function bareSimple(sailing: SimpleSailing): BareSimpleSailing {
  return {
    ...sailing,
    property: sailing.property._id,
    template: sailing.template ? sailing.template._id : undefined,
    days: sailing.days.map(bareSimpleSailingDay),
  };
}

export function unsimplifyBareSailing(
  sailing: BareSimpleSailing,
  feeds: BareFeedTemplate[][]
): BareSailing {
  return {
    ...sailing,
    days: sailing.days.map((d, i) => unsimplifyBareSailingDay(d, feeds[i])),
  };
}

export interface GetDailyProgramAdditionalFilters {
  eventsBySegmentIds?: string[];
  hoursByVenueTypeIds?: string[];
}

interface GetDailyProgramParams {
  baseUrl: string;
  endpoint: string;
  audience?: string;
  property: Property;
  plan: BarePlan;
  dayBundle?: SailingDayBundle;
  eventTemplates: BareEventTemplate[];
  eventTemplateTranslations: EventTemplateTranslation[];
  venueTemplates: BareVenueTemplate[];
  venueTemplateTranslations: VenueTemplateTranslation[];
  contentTemplates: BareContentTemplate[];
  contentTemplateTranslations: ContentTemplateTranslation[];
  commonTextTranslations: CommonTextTranslation[];
  venueTypeTranslations: VenueTypeTranslation[];
  mealPeriodTranslations: MealPeriodTranslation[];
  segmentTranslations: SegmentTranslation[];
  levelTranslations: LevelTranslation[];
  attributeTranslations: AttributeTranslation[];
  highlightTranslations: HighlightTranslation[];
  dayThemeTranslations: DayThemeTranslation[];
  languages: string[];
  defaultLanguage: string;
  selectedLanguage: string;
  publisherRole: GO_PUBLISHER_ROLE;
  images: BareImage[];
  imageRequirements?: ImageRequirement[];
  programSlotNumber?: number;
  settings?: GetDailyProgramSettings;
  additionalFilters?: GetDailyProgramAdditionalFilters;
  sliceEventsFromStart?: number;
  sliceEventsFromEnd?: number;
}

export function getDailyProgram(params: GetDailyProgramParams): DailyProgram {
  const hourFormat = params?.settings?.hourFormat ?? getHourFormat(params.property);
  const dayStart = params.plan.dayStart;
  const dayStartMinutes = timeToMinutes(dayStart);
  const levelLabel = getPropertyConfiguration(params.property.type, 'levelLabel');

  const additionalEventSegmentFilters = params.additionalFilters?.eventsBySegmentIds || [];
  const additionalVenueTypeFilters = params.additionalFilters?.hoursByVenueTypeIds || [];

  const {
    events: eventsInPlan,
    venueData: venueDataInPlan,
    content: contentsInPlan,
  } = getPlanPublishedData(params.plan, params.publisherRole);
  const feedInDay = params.dayBundle?.day.feeds[params.programSlotNumber ?? 0];

  const contentsInFeed = feedInDay ? findFeedContent(feedInDay) : [];
  const eventsInFeed = feedInDay ? findFeedEventContent(feedInDay) : [];
  const feedItemsBundle: AbstractAreaBundle[] = [...eventsInFeed, ...contentsInFeed];

  const currentDate = getDelayedGODate(dayStartMinutes);

  let translEvtTmpls = [...params.eventTemplates];
  let translVenTmpls = [...params.venueTemplates];
  let translCntTmpls = [...params.contentTemplates];
  let translVenTypes = [...params.property.brand.venueTypes];
  let translMealPeriods = [...params.property.brand.mealPeriods];
  let translSegments = [...params.property.brand.segments];
  let translLevels = [...params.property.brand.levels];
  let translAttributes = [...params.property.brand.attributes];
  let translHighlights = [...params.property.brand.eventHighlights];
  let translDayThemes = [...params.property.brand.dayThemes];

  if (params.selectedLanguage !== params.defaultLanguage) {
    translEvtTmpls = translEvtTmpls
      .map(et => {
        const translation = params.eventTemplateTranslations.find(ett =>
          safeIdCompare(ett.refers, et._id)
        );
        return translateEventTemplate(et, translation, params.selectedLanguage);
      })
      .filter(notUndefined);

    translVenTmpls = translVenTmpls.map(vt => {
      const translation = params.venueTemplateTranslations.find(vtt =>
        safeIdCompare(vtt.refers, vt._id)
      );
      return translateVenueTemplate(vt, translation, params.selectedLanguage) || vt;
    });

    translCntTmpls = translCntTmpls
      .map(ct => {
        const translation = params.contentTemplateTranslations.find(ctt =>
          safeIdCompare(ctt.refers, ct._id)
        );
        return translateBareContentTemplate(ct, translation, params.selectedLanguage);
      })
      .filter(notUndefined);

    translVenTypes = translVenTypes.map(vt => {
      const translation = params.venueTypeTranslations.find(vtt =>
        safeIdCompare(vtt.refers, vt._id)
      );
      return translateBrandVenueType(vt, translation, params.selectedLanguage) || vt;
    });

    translMealPeriods = translMealPeriods.map(mp => {
      const translation = params.mealPeriodTranslations.find(mpt =>
        safeIdCompare(mpt.refers, mp._id)
      );
      return translateMealPeriod(mp, translation, params.selectedLanguage) || mp;
    });

    translSegments = translSegments.map(sg => {
      const translation = params.segmentTranslations.find(sgt => safeIdCompare(sgt.refers, sg._id));
      return translateBrandSegment(sg, translation, params.selectedLanguage) || sg;
    });

    translLevels = translLevels.map(lv => {
      const translation = params.levelTranslations.find(lvt => safeIdCompare(lvt.refers, lv._id));
      return translateBrandLevel(lv, translation, params.selectedLanguage) || lv;
    });

    translAttributes = translAttributes.map(at => {
      const translation = params.attributeTranslations.find(att =>
        safeIdCompare(att.refers, at._id)
      );
      return translateBrandAttribute(at, translation, params.selectedLanguage) || at;
    });

    translHighlights = translHighlights.map(hi => {
      const translation = params.highlightTranslations.find(hit =>
        safeIdCompare(hit.refers, hi._id)
      );
      return translateBrandHighlight(hi, translation, params.selectedLanguage) || hi;
    });

    translDayThemes = translDayThemes.map(dt => {
      const translation = params.dayThemeTranslations.find(dtt =>
        safeIdCompare(dtt.refers, dt._id)
      );
      return translateBrandDayTheme(dt, translation, params.selectedLanguage) || dt;
    });
  } else {
    translEvtTmpls = translEvtTmpls.filter(et => !et.notNative);
  }

  const evtTmplMap = mapBy(translEvtTmpls, '_id');
  const venTmplMap = mapBy(translVenTmpls, '_id');
  const cntTmplMap = mapBy(translCntTmpls, '_id');

  const venueDataMap = mapBy(venueDataInPlan, 'template');
  const venueTypeMap = mapBy(translVenTypes, '_id');
  const mealPeriodMap = mapBy(translMealPeriods, '_id');
  const imgMap = mapBy(params.images, 'key');

  const commonTexts = getCommonTexts(
    {
      default: params.property.brand.commonTexts,
      translations: params.commonTextTranslations,
    },
    {
      selected:
        params.selectedLanguage === params.defaultLanguage ? undefined : params.selectedLanguage,
    }
  );

  const { sliceEventsFromStart, sliceEventsFromEnd } = params;

  const events: DailyProgramEvent[] = eventsInPlan
    .map(e => {
      const et = evtTmplMap[e.template];
      const vt = venTmplMap[e.venue];

      if (!et || et.archived || !vt || vt.archived || et.crewOnly || et.isPrivate) return undefined;

      if (!evaluateMcmEndpoint('visible', params.endpoint, e.mcm, et.mcm)) {
        return undefined;
      }

      if (
        et.segment &&
        additionalEventSegmentFilters.length > 0 &&
        !additionalEventSegmentFilters.includes(et.segment)
      ) {
        return undefined;
      }

      if (et.source && et.properties.length <= 1) {
        if (
          params.audience &&
          !evaluateAudienceZone('visible', params.audience, e.audience, et.audience)
        ) {
          return undefined;
        }

        // the === false is important here... if it's null, we want to keep it
        if (
          evaluateAudienceTruthyAggregate(
            'visible',
            params.property.zones.map(z => z._id),
            e.audience,
            et.audience
          ) === false
        ) {
          return undefined;
        }
      }

      if (sliceEventsFromStart !== undefined) {
        const startTimeCutoff = addMinutesToTime(dayStart, clamp(sliceEventsFromStart, 0, 24) * 60);
        if (
          sliceEventsFromStart !== 24 &&
          timeCompare(e.startTime, startTimeCutoff, dayStartMinutes) >= 0
        ) {
          return undefined;
        }
      }

      if (sliceEventsFromEnd !== undefined) {
        const startTimeCutoff = addMinutesToTime(dayStart, -clamp(sliceEventsFromEnd, 0, 24) * 60);
        if (
          sliceEventsFromEnd !== 24 &&
          timeCompare(e.startTime, startTimeCutoff, dayStartMinutes) < 0
        ) {
          return undefined;
        }
      }

      const [replacedTitle] = evaluateMergedVars(
        et.title,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        e.mergedVars
      );

      const [replacedDesc] = evaluateMergedVars(
        et.description,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        e.mergedVars
      );

      const timeDescription = generateExtendedTimeDescription(
        e.startTime,
        e.duration,
        hourFormat,
        params.settings?.eventTimeStringStyle || 'locale',
        commonTexts,
        e.interactiveFeedDisplay,
        et.interactiveFeedDisplay
      );

      let evtStartTimeMins = timeToMinutes(e.startTime);
      if (evtStartTimeMins < dayStartMinutes) {
        evtStartTimeMins += 24 * 60;
      }

      let evtEndTimeMins = evtStartTimeMins + e.duration;

      const eventVenueDisplay = getEventVenueDisplay(
        e,
        vt,
        commonTexts,
        params.settings?.venueStringStyle || 'name-only'
      );

      let segment = params.property.brand.segments.find(el => safeIdCompare(et.segment, el._id));
      let levels = params.property.brand.levels.filter(
        lv => et.levels.findIndex(l => safeIdCompare(l, lv._id)) > -1
      );
      let attributes = params.property.brand.attributes.filter(
        at => et.attributes.findIndex(a => safeIdCompare(a, at._id)) > -1
      );
      let highlights = params.property.brand.eventHighlights.filter(
        eh => (e.highlights || []).findIndex(h => safeIdCompare(h, eh._id)) > -1
      );
      let zones = params.audience
        ? []
        : params.property.zones.filter(z =>
            evaluateAudienceZone('visible', z._id, e.audience, et.audience)
          );

      const images: DailyProgramImage[] = e.imageOverride
        ? [{ url: e.imageOverride }]
        : et.images.map(img => ({
            url: img,
          }));

      const imageOptimizations = getDailyProgramImageOptimizations(
        images,
        imgMap,
        params.imageRequirements
      );

      const result: DailyProgramEvent = {
        id: e._id,
        templateId: et._id,
        title: replacedTitle,
        description: replacedDesc,
        venueString: eventVenueDisplay.venueString,
        venueDisplay: eventVenueDisplay,
        startTime: e.startTime,
        timeString: timeDescription.timeString,
        isTrending: evaluateMcmEndpoint('trending', params.endpoint, e.mcm, et.mcm),
        isUpcoming: evtStartTimeMins - currentDate.minutes > -15,
        isInProgress:
          currentDate.minutes >= evtStartTimeMins && currentDate.minutes <= evtEndTimeMins,
        isPriced: !!et.priced,
        venueId: e.venue,
        callToActions: evaluateCallToActions(e.callToActions, et.callToActions),
        segmentId: segment?._id,
        levelIds: levels.map(lv => lv._id),
        attributeIds: attributes.map(at => at._id),
        highlightIds: highlights.map(hi => hi._id),
        zoneIds: zones.map(z => z._id),
        interactiveFeedDisplay: e.interactiveFeedDisplay,
        duration: e.duration,
        deckDisplayName: e.deckDisplayName,
        ...makeDailyProgramImages(images, imageOptimizations, params.baseUrl),
        _mergedVars: e.mergedVars,
        _timeDescription: timeDescription,
      };

      return result;
    })
    .filter(notUndefined)
    .sort((e1, e2) => {
      const fallbackSort =
        timeToMinutes(e1.startTime, dayStartMinutes) - timeToMinutes(e2.startTime, dayStartMinutes);
      if (e1.isTrending && e2.isTrending) {
        return fallbackSort;
      }

      if (e1.isTrending) {
        return -1;
      }

      if (e2.isTrending) {
        return 1;
      }

      return fallbackSort;
    });

  const venues: DailyProgramVenue[] = translVenTmpls
    .map(vt => {
      if (vt.archived || vt.isHidden) return undefined;

      const vd = venueDataMap[vt._id] || makeEmptyVenueDisposition(vt._id);

      if (!evaluateMcmEndpoint('visible', params.endpoint, vd.mcm, vt.mcm)) {
        return undefined;
      }

      if (
        params.audience &&
        !evaluateAudienceZone('visible', params.audience, vd.audience, vt.audience)
      ) {
        return undefined;
      }

      if (
        evaluateAudienceTruthyAggregate(
          'visible',
          params.property.zones.map(z => z._id),
          vd.audience,
          vt.audience
        ) === false
      ) {
        return undefined;
      }

      const types = params.property.brand.venueTypes
        .filter(bvt => vt.types.findIndex(t => safeIdCompare(t, bvt._id)) > -1)
        .filter(bvt => evaluateMcmEndpoint('visible', params.endpoint, bvt.mcm, undefined))
        .filter(bvt =>
          additionalVenueTypeFilters.length > 0
            ? additionalVenueTypeFilters.includes(bvt._id)
            : true
        )
        .map(bvt => venueTypeMap[bvt._id] || bvt);

      const zones = params.audience
        ? []
        : params.property.zones.filter(z =>
            evaluateAudienceZone('visible', z._id, vd.audience, vt.audience)
          );

      if (types.length < 1) {
        return undefined;
      }

      const availabilities = getVenueAvailabilityTimePairs(
        vd.hours,
        dayStartMinutes,
        hourFormat,
        params?.settings?.venueTimeStringStyle || 'locale',
        commonTexts,
        vd.displayOptions?.timeDisplay,
        params?.settings?.keepVenueAvailabilityTimePairs
      );

      const { isOpen } = getVenueStatus(dayStartMinutes, currentDate.minutes, availabilities);

      const vDisp = getVenueDisplay(
        vd,
        vt,
        commonTexts,
        params.settings?.venueStringStyle || 'name-only'
      );

      const mealPeriods = [...vd.mealPeriods].filter(mp => mealPeriodMap[mp.brandMealPeriodId]);
      mealPeriods.sort(makeBareEventSorter(dayStartMinutes));

      const mealPeriodStrings = mealPeriods.map(mp => {
        const startStr = minutesToTimeString(
          timeToMinutes(mp.startTime),
          hourFormat,
          params.settings?.eventTimeStringStyle || 'locale'
        );
        const endStr = minutesToTimeString(
          timeToMinutes(mp.endTime),
          hourFormat,
          params.settings?.eventTimeStringStyle || 'locale'
        );

        const timeString = [startStr, endStr].join(' - ');

        return { name: mp.name, timeString };
      });

      const timeStrings = getVenueAvailabilityStrings(
        vd.hours,
        dayStartMinutes,
        hourFormat,
        'locale',
        commonTexts,
        vd.displayOptions?.timeDisplay
      );

      const images: DailyProgramImage[] = vt.images.map(img => ({
        url: img,
      }));

      const imageOptimizations = getDailyProgramImageOptimizations(
        images,
        imgMap,
        params.imageRequirements
      );

      const result: DailyProgramVenue = {
        id: vd._id,
        templateId: vt._id,
        name: vt.name,
        description: vt.description,
        order: vt.order,
        location: vDisp.location,
        venueString: vDisp.venueString,
        isTrending: evaluateMcmEndpoint('trending', params.endpoint, vd.mcm, vt.mcm),
        isOpen,
        typeIds: types.map(t => t._id),
        zoneIds: zones.map(z => z._id),
        callToActions: evaluateCallToActions(vd.callToActions, vt.callToActions),
        timeStrings,
        mealPeriodStrings,
        ...makeDailyProgramImages(images, imageOptimizations, params.baseUrl),
        _availabilities: availabilities,
      };

      return result;
    })
    .filter(notUndefined)
    .sort((v1, v2) => {
      const fallbackSort = v1.order - v2.order;

      if (v1.isTrending && v2.isTrending) {
        return fallbackSort;
      }

      if (v1.isTrending) {
        return -1;
      }

      if (v2.isTrending) {
        return 1;
      }

      return fallbackSort;
    });

  const offers: DailyProgramOffer[] = contentsInPlan
    .map(c => {
      const ct = cntTmplMap[c.template];
      if (!ct || ct.archived || ct.type !== 'offer') return undefined;

      const vt = c.venue ? venTmplMap[c.venue] : undefined;

      if (!evaluateMcmEndpoint('visible', params.endpoint, c.mcm, ct.mcm)) {
        return undefined;
      }

      if (ct.source && ct.properties.length <= 1) {
        if (
          params.audience &&
          !evaluateAudienceZone('visible', params.audience, c.audience, ct.audience)
        ) {
          return undefined;
        }

        if (
          evaluateAudienceTruthyAggregate(
            'visible',
            params.property.zones.map(z => z._id),
            c.audience,
            ct.audience
          ) === false
        ) {
          return undefined;
        }
      }

      const currentTime = dateToTime(new Date());

      if (
        timeCompare(c.startTime, currentTime, dayStartMinutes) > 0 ||
        timeCompare(addMinutesToTime(c.startTime, c.duration), currentTime, dayStartMinutes) < 0
      ) {
        return undefined;
      }

      const [replacedTitle] = evaluateMergedVars(
        ct.name,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        c.mergedVars
      );

      const [replacedDesc] = evaluateMergedVars(
        ct.description,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        c.mergedVars
      );

      let venueString = '';
      if (vt) {
        const vd = venueDataMap[vt._id] || makeEmptyVenueDisposition(vt._id);
        venueString = getVenueDisplay(
          vd,
          vt,
          commonTexts,
          params.settings?.venueStringStyle || 'name-only'
        ).venueString;
      }

      const images: DailyProgramImage[] = ct.images.map(img => ({
        url: img,
      }));

      const imageOptimizations = getDailyProgramImageOptimizations(
        images,
        imgMap,
        params.imageRequirements
      );

      const result: DailyProgramOffer = {
        id: c._id,
        templateId: ct._id,
        title: replacedTitle,
        description: replacedDesc,
        startTime: c.startTime,
        venueString,
        isTrending: evaluateMcmEndpoint('trending', params.endpoint, c.mcm, ct.mcm),
        typeIds: [],
        tagIds: ct.tags,
        zoneIds: params.audience
          ? []
          : params.property.zones
              .filter(z => evaluateAudienceZone('visible', z._id, c.audience, ct.audience))
              .map(z => z._id),
        venueId: c.venue,
        callToActions: evaluateCallToActions(c.callToActions, ct.callToActions),
        ...makeDailyProgramImages(images, imageOptimizations, params.baseUrl),
      };

      return result;
    })
    .filter(notUndefined)
    .sort((c1, c2) => {
      const fallbackSort =
        timeToMinutes(c1.startTime, dayStartMinutes) - timeToMinutes(c2.startTime, dayStartMinutes);

      if (c1.isTrending && c2.isTrending) {
        return fallbackSort;
      }

      if (c1.isTrending) {
        return -1;
      }

      if (c2.isTrending) {
        return 1;
      }

      return fallbackSort;
    });

  const messages: DailyProgramMessage[] = contentsInPlan
    .map(c => {
      const ct = cntTmplMap[c.template];
      if (!ct || ct.archived || ct.type !== 'message') return undefined;

      if (!evaluateMcmEndpoint('visible', params.endpoint, c.mcm, ct.mcm)) {
        return undefined;
      }

      if (ct.source && ct.properties.length <= 1) {
        if (
          params.audience &&
          !evaluateAudienceZone('visible', params.audience, c.audience, ct.audience)
        ) {
          return undefined;
        }

        if (
          evaluateAudienceTruthyAggregate(
            'visible',
            params.property.zones.map(z => z._id),
            c.audience,
            ct.audience
          ) === false
        ) {
          return undefined;
        }
      }

      const [replacedTitle] = evaluateMergedVars(
        ct.name,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        c.mergedVars
      );

      const [replacedDesc] = evaluateMergedVars(
        ct.description,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        c.mergedVars
      );

      const result: DailyProgramMessage = {
        id: c._id,
        templateId: ct._id,
        title: replacedTitle,
        description: replacedDesc,
        isTrending: evaluateMcmEndpoint('trending', params.endpoint, c.mcm, ct.mcm),
        zoneIds: params.audience
          ? []
          : params.property.zones
              .filter(z => evaluateAudienceZone('visible', z._id, c.audience, ct.audience))
              .map(z => z._id),
        callToActions: evaluateCallToActions(c.callToActions, ct.callToActions),
      };

      return result;
    })
    .filter(notUndefined)
    .sort((c1, c2) => (c1.isTrending ? -1 : c2.isTrending ? 1 : 0));

  const infos: DailyProgramInformation[] = contentsInPlan
    .map(c => {
      const ct = cntTmplMap[c.template];
      if (!ct || ct.archived || ct.type !== 'info') return undefined;

      if (!evaluateMcmEndpoint('visible', params.endpoint, c.mcm, ct.mcm)) {
        return undefined;
      }

      if (ct.source && ct.properties.length <= 1) {
        if (
          params.audience &&
          !evaluateAudienceZone('visible', params.audience, c.audience, ct.audience)
        ) {
          return undefined;
        }

        if (
          evaluateAudienceTruthyAggregate(
            'visible',
            params.property.zones.map(z => z._id),
            c.audience,
            ct.audience
          ) === false
        ) {
          return undefined;
        }
      }

      const [replacedTitle] = evaluateMergedVars(
        ct.name,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        c.mergedVars
      );

      const [replacedDesc] = evaluateMergedVars(
        ct.description,
        params.dayBundle,
        params.languages,
        params.selectedLanguage,
        params.defaultLanguage,
        c.mergedVars
      );

      const result: DailyProgramInformation = {
        id: c._id,
        templateId: ct._id,
        title: replacedTitle,
        description: replacedDesc,
        isTrending: evaluateMcmEndpoint('trending', params.endpoint, c.mcm, ct.mcm),
        appearance: ct.appearance,
        zoneIds: params.audience
          ? []
          : params.property.zones
              .filter(z => evaluateAudienceZone('visible', z._id, c.audience, ct.audience))
              .map(z => z._id),
        callToActions: evaluateCallToActions(c.callToActions, ct.callToActions),
      };

      return result;
    })
    .filter(notUndefined)
    .sort((c1, c2) => {
      const fallbackSort = c1.isTrending ? -1 : c2.isTrending ? 1 : 0;

      if (c1.appearance === c2.appearance) {
        return fallbackSort;
      }

      if (c1.appearance === 'questionAndAnswer') {
        return 1;
      }

      if (c2.appearance === 'questionAndAnswer') {
        return -1;
      }

      return fallbackSort;
    });

  const legacyContents: DailyProgramLegacyContent[] = feedItemsBundle
    .map(c => {
      switch (c.type) {
        case 'event': {
          const eventInPlan = events.find(e => safeIdCompare(e.templateId, c.eventId));
          if (!eventInPlan) return undefined;

          const { title, description } = eventInPlan;

          const result: DailyProgramLegacyContent = {
            id: c.feedArea._id,
            templateId: '',
            printHtml: `<h2 class="EventBlurbHeader">${title}</h2>
            <p class="EventBlurbContent">${description}</p>`,
          };

          return result;
        }
        case 'content': {
          const ct = cntTmplMap[c.contentId];
          if (!ct || ct.archived || ct.hideFromDigitalDailyProgram) return undefined;

          const [replacedHtml] = evaluateMergedVars(
            ct.content,
            params.dayBundle,
            params.languages,
            params.selectedLanguage,
            params.defaultLanguage,
            c.feedArea.content as any
          );

          const result: DailyProgramLegacyContent = {
            id: c.feedArea._id,
            templateId: ct._id,
            printHtml: replacedHtml,
          };

          return result;
        }
      }
    })
    .filter(notUndefined);

  const program: DailyProgram = {
    date: params.dayBundle?.dateIso ?? getDelayedGODate(dayStartMinutes).iso,
    dayBundle: params.dayBundle,
    collections: {
      eventTemplates: translEvtTmpls,
      venueTemplates: translVenTmpls,
      contentTemplates: translCntTmpls,
      segments: translSegments,
      levels: translLevels,
      attributes: translAttributes,
      highlights: translHighlights,
      dayThemes: translDayThemes,
      venueTypes: translVenTypes,
      zones: params.audience ? [] : params.property.zones,
      offerTypes: [],
      levelLabel,
      commonTexts,
    },
    events,
    venues,
    offers,
    messages,
    infos,
    legacyContents,
  };

  return program;
}
